import { ExtranetOrderScheduleDeadlineDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacTableColumn, PharmacText } from '@pharmac/ui'
import { weekdayShortTranslations } from '../customer-groups/util/date-utils'
import { OrderSchedulesCustomerGroupTable } from './OrderSchedulesCustomerGroupTable'

type OrderSchedulesTableRowProps = {
  orderSchedules: ExtranetOrderScheduleDeadlineDto
}

export const OrderSchedulesTableRow = ({ orderSchedules }: OrderSchedulesTableRowProps) => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage()

  const allOffFromDate =
    orderSchedules.customerGroups.filter(
      (group) =>
        group.isSkipped ||
        group.exceptions?.find(
          (exception) => exception.exceptionDeadlineDate !== group.deadlineDate
        )
    ).length === orderSchedules.customerGroups.length

  const getWeekDays = (dateString: string) => {
    const date = new Date(dateString)
    const day = date.getDay() - 1
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

    return String(t(weekdayShortTranslations(weekDays[day]) as any)).toLocaleUpperCase()
  }

  return (
    <>
      <ExtranetTableRow
        style={{
          backgroundColor: 'var(--color-accent-2)',
          fontWeight: 800,
        }}
        expand={
          <ExtranetContainer
            paddingSmall
            style={{ backgroundColor: 'var(--color-accent-1)' }}
          >
            <OrderSchedulesCustomerGroupTable customerGroups={orderSchedules.customerGroups} />
          </ExtranetContainer>
        }
        toggleExpand={() => null}
        children={({ isExpanded }) => (
          <ExtranetTableGroupColumn>
            <ExtranetTableRow>
              <ExtranetTableColumn
                size={1}
                style={{
                  justifyContent: 'center',
                  backgroundColor: !orderSchedules.hasExceptions
                    ? 'var(--color-success)'
                    : allOffFromDate
                      ? 'var(--color-accent-4)'
                      : 'var(--color-warning)',
                }}
              >
                <ExtranetContainer full>
                  <PharmacIcon
                    icon={
                      !orderSchedules.hasExceptions
                        ? 'checkCircle'
                        : allOffFromDate
                          ? 'chevronsLeft'
                          : 'info'
                    }
                  />
                </ExtranetContainer>
              </ExtranetTableColumn>
              <ExtranetTableColumn
                size={4}
                style={{
                  justifyContent: 'center',
                }}
              >
                <PharmacText weight="bold">
                  {getWeekDays(orderSchedules.deadlineDate)}{' '}
                  {formatDateString(orderSchedules.deadlineDate)} {orderSchedules.deadlineTime}
                </PharmacText>
              </ExtranetTableColumn>
              <ExtranetTableColumn size={2}>
                <ExtranetContainer
                  alignCenter
                  flexRow
                >
                  <PharmacIcon
                    icon="users"
                    style={{
                      color: allOffFromDate ? undefined : 'var(--color-primary)',
                    }}
                  />
                  <PharmacText
                    paddingSmallLeft
                    weight="bold"
                  >
                    {orderSchedules.customerGroupCount}
                  </PharmacText>
                </ExtranetContainer>
              </ExtranetTableColumn>
              {minDesktop && (
                <ExtranetTableColumn size={8}>
                  <ExtranetContainer
                    alignCenter
                    flexRow
                  >
                    {orderSchedules.hasExceptions && (
                      <>
                        <PharmacText
                          weight="bold"
                          paddingSmallRight
                        >
                          {t('extranet:orderSchedules.includesExceptions')}:
                        </PharmacText>

                        {orderSchedules.customerGroups.find(
                          (group) =>
                            !group.isDouble && group.exceptions.length > 0 && !group.isSkipped
                        ) && (
                          <PharmacText
                            weight="bold"
                            paddingSmallRight
                          >
                            {t('extranet:orderSchedules.transfer')}
                          </PharmacText>
                        )}
                        {orderSchedules.customerGroups.find((group) => group.isDouble) && (
                          <PharmacText
                            weight="bold"
                            paddingSmallRight
                          >
                            {t('extranet:orderSchedules.double')}
                          </PharmacText>
                        )}
                        {orderSchedules.customerGroups.find(
                          (group) => !group.isDouble && group.isSkipped
                        ) && (
                          <PharmacText weight="bold">
                            {t('extranet:orderSchedules.removed')}
                          </PharmacText>
                        )}
                      </>
                    )}
                  </ExtranetContainer>
                </ExtranetTableColumn>
              )}
              <ExtranetExpandArrowColumn>
                <ExtranetCatalogItemChevron isExpanded={isExpanded} />
              </ExtranetExpandArrowColumn>
            </ExtranetTableRow>
            {!minDesktop && orderSchedules.hasExceptions && (
              <ExtranetTableRow
                noMargin
                noBorders
                style={{
                  borderTop: '2px dashed var(--color-accent-4)',
                }}
              >
                <PharmacTableColumn size={7}>
                  <ExtranetContainer full>
                    {orderSchedules.hasExceptions && (
                      <>
                        <PharmacText
                          weight="bold"
                          paddingSmallRight
                        >
                          {t('extranet:orderSchedules.includesExceptions')}:
                        </PharmacText>
                        {orderSchedules.customerGroups.find(
                          (group) =>
                            !group.isDouble && group.exceptions.length > 0 && !group.isSkipped
                        ) && (
                          <PharmacText
                            weight="bold"
                            paddingSmallRight
                          >
                            {t('extranet:orderSchedules.transfer')}
                          </PharmacText>
                        )}
                        {orderSchedules.customerGroups.find((group) => group.isDouble) && (
                          <PharmacText weight="bold">
                            {t('extranet:orderSchedules.double')}
                          </PharmacText>
                        )}
                        {orderSchedules.customerGroups.find(
                          (group) => !group.isDouble && group.isSkipped
                        ) && (
                          <PharmacText weight="bold">
                            {t('extranet:orderSchedules.removed')}
                          </PharmacText>
                        )}
                      </>
                    )}
                  </ExtranetContainer>
                </PharmacTableColumn>
              </ExtranetTableRow>
            )}
          </ExtranetTableGroupColumn>
        )}
      />
    </>
  )
}
